import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from '../setupAxios';
import { apiUrl } from '../config/api';

const AuthContext = createContext(null);
export const ROLE_SUPER_ADMIN = 1;

function readStoredAuth() {
  const token = localStorage.getItem('token');
  const username = localStorage.getItem('username');
  const actk = localStorage.getItem('actk');
  const roleRaw = localStorage.getItem('role');
  const role = roleRaw != null ? Number(roleRaw) : 0;
  if (!token) {
    return { token: null, username: null, actk: null, role: 0 };
  }
  return { token, username: username || null, actk: actk || null, role: Number.isNaN(role) ? 0 : role };
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(readStoredAuth);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const handleUnauthorized = () => {
      localStorage.removeItem('role');
      localStorage.removeItem('actk');
      setAuth({ token: null, username: null, actk: null, role: 0 });
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setReady(true);
      return undefined;
    }

    let cancelled = false;

    axios
      .get(apiUrl('/api/auth/me'), {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (cancelled) return;
        const username = response.data?.username || localStorage.getItem('username');
        const role = response.data?.role != null ? Number(response.data.role) : 0;
        const actk = response.data?.actk || localStorage.getItem('actk');
        if (username) {
          localStorage.setItem('username', username);
        }
        localStorage.setItem('role', String(role));
        if (actk) {
          localStorage.setItem('actk', actk);
        }
        setAuth({ token, username: username || null, actk: actk || null, role });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('username');
          localStorage.removeItem('role');
          localStorage.removeItem('actk');
          setAuth({ token: null, username: null, actk: null, role: 0 });
        } else {
          setAuth(readStoredAuth());
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback((token, username, role = 0, actk = null) => {
    const roleValue = role != null && !Number.isNaN(Number(role)) ? Number(role) : 0;
    localStorage.setItem('token', token);
    localStorage.setItem('username', username);
    localStorage.setItem('role', String(roleValue));
    if (actk) {
      localStorage.setItem('actk', actk);
    } else {
      localStorage.removeItem('actk');
    }
    setAuth({ token, username, actk: actk || null, role: roleValue });
  }, []);

  const logout = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await axios.post(
          apiUrl('/api/auth/logout'),
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error('Logout error:', err);
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('actk');
    setAuth({ token: null, username: null, actk: null, role: 0 });
  }, []);

  const value = useMemo(
    () => ({
      token: auth.token,
      actk: auth.actk,
      username: auth.username,
      role: auth.role,
      isAuthenticated: !!auth.token,
      isSuperAdmin: Number(auth.role) === ROLE_SUPER_ADMIN,
      ready,
      login,
      logout,
    }),
    [auth, ready, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
